import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import {
  AutomationDeliveryProvider,
  AutomationTemplateIds,
  type AutomationId,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import type { AutomationArtifactId } from "@repo/app-types/entities/automationArtifact";
import type { AutomationRunId } from "@repo/app-types/entities/automationRun";
import {
  managerReportDelegateInputSchema,
  managerReportNotifyOutputSchema,
  managerReportPlanOutputSchema,
  type ManagerReportNotifyOutput,
} from "@repo/app-types/entities/managerReportOrchestration";
import {
  buildReportThreadId,
  decodeInstanceReportKey,
  REPORT_DELEGATE_TOOL_ID,
  REPORT_NOTIFY_TOOL_ID,
  REPORT_PLAN_TOOL_ID,
  reportDelegateInputSchema,
  reportNotifyOutputSchema,
  reportPlanOutputSchema,
  SHARED_REPORT_KEY,
  type NotifyRecipientReason,
  type ReportDelegateOutput,
  type ReportNotifyOutput,
  type ReportNotifyTarget,
} from "@repo/app-types/entities/reportOrchestration";
import type { ChatToolComponents } from "@repo/ui/chat/chatMessage";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ChatToolStatus,
  type ToolComponent,
  type ToolComponentProps,
} from "@repo/ui/chat/chatToolCall";
import type { UIMessage } from "ai";
import { useMemo, type FC } from "react";
import { FallbackTool } from "../agents/tools/fallbackTool.js";
import {
  guardToolRender,
  RESEARCH_THREAD_TOOLS,
  RunTranscript,
} from "./runTranscript.js";
import {
  RunTranscriptProvider,
  useRunTranscriptContext,
  type ResolveThreadMessages,
} from "./runTranscriptContext.js";
import { SubagentTranscript } from "./subagentTranscript.js";

/** "1 manager" / "2 managers" — keeps tool labels grammatical. */
function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/** Display labels for delivery providers in progress rows. */
const DELIVERY_PROVIDER_LABELS: Record<AutomationDeliveryProvider, string> = {
  [AutomationDeliveryProvider.EMAIL]: "Email",
  [AutomationDeliveryProvider.SLACK]: "Slack",
  [AutomationDeliveryProvider.TEAMS]: "Microsoft Teams",
};

function describeRecipientsWithoutChatUser(
  recipients: readonly { teammate_id: string; email: string }[],
  teammateNamesById: ReadonlyMap<string, string>,
  providerLabel: string,
): string | null {
  if (recipients.length === 0) {
    return null;
  }
  const who = recipients
    .map(({ teammate_id, email }) => {
      const name = teammateNamesById.get(teammate_id);
      return name ? `${name} (${email})` : email;
    })
    .join(", ");
  return `skipped ${who} — not on ${providerLabel}`;
}

/**
 * Per-template copy for the plan and delegation rows. The status machinery is
 * shared ({@link makePlanTool} / {@link makeDelegateTool}); only the words
 * differ — the manager-report templates speak of managers and their subject
 * noun, the custom template of its single report.
 */
interface PlanCopy {
  running: string;
  failed: string;
  done: (reportNames: readonly string[]) => string;
}

interface DelegationCopy {
  /** Resolves the row's display name from the delegated report key. */
  resolveName: (
    reportKey: string,
    teammateNamesById: ReadonlyMap<string, string>,
    reportNamesByKey: ReadonlyMap<string, string>,
  ) => string;
  /** `name` is null while the call's input is still streaming. */
  running: (name: string | null) => string;
  generated: (name: string) => string;
  empty: (name: string) => string;
  failed: (name: string) => string;
  /** The report dropped out of the run's scope after planning — released,
   * not failed (mirrors the outcome contract). */
  released: (name: string) => string;
  /** A synthesis declined because its input reports aren't all settled yet. */
  notReady: (name: string) => string;
}

/**
 * The delegation row's label and effective status, from the tool call itself:
 * running while the input has no output, then the saved report's emptiness or
 * the call's failure.
 */
function describeDelegation(
  name: string,
  status: ChatToolStatus,
  output: ReportDelegateOutput | undefined,
  copy: DelegationCopy,
): { label: string; status: ChatToolStatus } {
  if (status.type === "incomplete") {
    return { label: copy.failed(name), status };
  }
  if (status.type === "complete") {
    if (output !== undefined && !checkIsErrorToolOutput(output)) {
      if (output.status === "saved") {
        return {
          label: output.is_empty ? copy.empty(name) : copy.generated(name),
          status,
        };
      }
      if (output.status === "not_ready") {
        return { label: copy.notReady(name), status };
      }
      return { label: copy.released(name), status };
    }
    return { label: copy.failed(name), status: { type: "incomplete" } };
  }
  return { label: copy.running(name), status: { type: "running" } };
}

// The orchestrator's tool renderers are static module-level components: they
// read the run's ambient values from RunTranscriptContext instead of closing
// over props, so their identities are stable and a streaming update re-renders
// (rather than remounts) the transcript — remounting would tear down the
// nested live subagent streams.

/** The plan output's reports as (key, name) pairs, in either persisted shape. */
export function plannedReports(
  output: unknown,
): { key: string; name: string }[] | null {
  const current = reportPlanOutputSchema.safeParse(output);
  if (current.success && !checkIsErrorToolOutput(current.data)) {
    return current.data.reports.map((report) => ({
      key: report.report_key,
      name: report.name,
    }));
  }
  const legacy = managerReportPlanOutputSchema.safeParse(output);
  return legacy.success && !checkIsErrorToolOutput(legacy.data)
    ? legacy.data.recipients.map((recipient) => ({
        key: recipient.teammate_id,
        name: recipient.name,
      }))
    : null;
}

export function buildReportNamesByKey(
  messages: readonly UIMessage[],
): ReadonlyMap<string, string> {
  const names = new Map<string, string>();
  for (const message of messages) {
    for (const part of message.parts) {
      // The AI SDK encodes each tool call as a message part typed
      // `tool-${toolName}` (or `dynamic-tool` with the name in `toolName`),
      // so this matches every plan_reports call in the conversation. Each
      // one's `output` is the plan the run committed to — parsing its
      // (report_key, name) pairs is what determines the reports this map
      // knows about; a call still streaming has no `output` yet and a later
      // plan's entries overwrite an earlier one's.
      const isPlanPart =
        part.type === `tool-${REPORT_PLAN_TOOL_ID}` ||
        (part.type === "dynamic-tool" && part.toolName === REPORT_PLAN_TOOL_ID);
      if (!isPlanPart || !("output" in part)) {
        continue;
      }
      for (const report of plannedReports(part.output) ?? []) {
        names.set(report.key, report.name);
      }
    }
  }
  return names;
}

/** "Tom, Mary, +3 more" — caps a name list so plan rows stay one line. */
function formatNameList(names: readonly string[], maxNames = 5): string {
  const shown = names.slice(0, maxNames);
  const overflow = names.length - shown.length;
  return overflow > 0
    ? `${shown.join(", ")}, +${overflow} more`
    : shown.join(", ");
}

/** The report key a delegation call targeted, or null while it streams. */
export function delegationReportKey(input: unknown): string | null {
  const current = reportDelegateInputSchema.safeParse(input);
  if (current.success) {
    return current.data.report_key;
  }
  const legacy = managerReportDelegateInputSchema.safeParse(input);
  return legacy.success ? legacy.data.recipient_teammate_id : null;
}

/** The plan renderer, parameterized by per-template copy. */
function makePlanTool(copy: PlanCopy): ToolComponent<unknown, unknown> {
  return ({ status, output }) => {
    if (status.type === "complete") {
      // A failed call completes with an error-shaped output (the tool catches
      // and returns its errors) — don't leave it spinning.
      if (output !== undefined && !checkIsErrorToolOutput(output)) {
        const reports = plannedReports(output);
        if (reports !== null) {
          return (
            <ChatToolBase status={status}>
              {copy.done(reports.map((report) => report.name))}
            </ChatToolBase>
          );
        }
      }
    }
    if (status.type !== "running") {
      return (
        <ChatToolBase status={{ type: "incomplete" }}>
          {copy.failed}
        </ChatToolBase>
      );
    }
    return <ChatToolBase status={status}>{copy.running}</ChatToolBase>;
  };
}

/**
 * The delegation renderer, parameterized by per-template copy. Returns a
 * stable module-level component per template (built once below) so streaming
 * updates re-render rather than remount the nested subagent.
 */
function makeDelegateTool(
  copy: DelegationCopy,
): ToolComponent<unknown, ReportDelegateOutput> {
  return ({ status, input, output }) => {
    const { runId, teammateNamesById, reportNamesByKey } =
      useRunTranscriptContext();
    const reportKey = delegationReportKey(input);
    if (reportKey === null) {
      // Input still streaming — no report to attribute the thread to yet.
      return (
        <ChatToolBase status={{ type: "running" }}>
          {copy.running(null)}
        </ChatToolBase>
      );
    }
    const name = copy.resolveName(
      reportKey,
      teammateNamesById,
      reportNamesByKey,
    );
    const row = describeDelegation(name, status, output, copy);
    return (
      <SubagentTranscript
        threadId={buildReportThreadId(runId, reportKey)}
        label={row.label}
        status={row.status}
      />
    );
  };
}

/**
 * The note shown beside a teammate whose copy didn't land. Keyed by the whole
 * reason enum so adding a reason to the schema fails typecheck here rather than
 * silently rendering the wrong note.
 */
const NOTIFY_REASON_NOTES: Record<
  NotifyRecipientReason,
  (providerLabel: string) => string
> = {
  send_error: () => "delivery failed",
  user_not_found: (providerLabel) => `not on ${providerLabel}`,
  empty: () => "no findings",
  unresolved: () => "no longer a recipient",
  preview: () => "preview run",
};

/** One recipient's display name plus the short reason note shown beside it. */
function describeNotifyTarget(
  target: ReportNotifyTarget,
  providerLabel: string,
  teammateNamesById: ReadonlyMap<string, string>,
): { name: string; note: string | null } {
  if (target.kind === "channel") {
    return {
      name: target.channel.name || "a channel",
      note:
        target.outcome === "failed"
          ? "post failed"
          : target.outcome === "skipped"
            ? "preview run"
            : null,
    };
  }
  return {
    name:
      teammateNamesById.get(target.teammate_id) ??
      target.email ??
      "Unknown teammate",
    // Present exactly when the copy didn't land — a delivered target has none.
    note: target.reason
      ? NOTIFY_REASON_NOTES[target.reason](providerLabel)
      : null,
  };
}

/**
 * Collapses the pass's per-(report, target) rows to one row per landing spot.
 * A subscriber copied on every report — or a channel posted to for every
 * report — settles the same way on each one, so listing them once per report
 * would read as that many distinct people. Kept per reason, so a teammate
 * skipped on one report and delivered on another still shows under both.
 */
function dedupeNotifyTargets(
  targets: readonly ReportNotifyTarget[],
): ReportNotifyTarget[] {
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key =
      target.kind === "teammate"
        ? `t:${target.teammate_id}:${target.reason ?? ""}`
        : `c:${target.channel.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** The notify pass's targets grouped by outcome — delivered, failed, skipped. */
const NotifyTargetsBreakdown: FC<{
  targets: readonly ReportNotifyTarget[];
  providerLabel: string;
  teammateNamesById: ReadonlyMap<string, string>;
}> = ({ targets, providerLabel, teammateNamesById }) => {
  const groups = (
    [
      { key: "delivered", label: "Delivered" },
      { key: "failed", label: "Failed" },
      { key: "skipped", label: "Skipped" },
    ] as const
  )
    .map((group) => ({
      ...group,
      items: dedupeNotifyTargets(
        targets.filter((target) => target.outcome === group.key),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="space-y-3 text-xs">
      {groups.map((group) => (
        <div key={group.key} className="space-y-1">
          <div className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
            {group.label} ({group.items.length})
          </div>
          <ul className="space-y-0.5">
            {group.items.map((target, index) => {
              const { name, note } = describeNotifyTarget(
                target,
                providerLabel,
                teammateNamesById,
              );
              return (
                <li
                  key={
                    target.kind === "teammate"
                      ? `t-${target.teammate_id}-${index}`
                      : `c-${target.channel.id}-${index}`
                  }
                  className="flex items-baseline gap-1.5"
                >
                  <span className="truncate font-medium text-foreground">
                    {name}
                  </span>
                  {note ? (
                    <span className="shrink-0 text-muted-foreground">
                      — {note}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

function checkIsDeliveredTeammate(target: ReportNotifyTarget): boolean {
  return target.kind === "teammate" && target.outcome === "delivered";
}

/**
 * A teammate send that failed for any reason. Provider-user resolution and
 * transport errors share the same retry/failure contract.
 */
function checkIsFailedTeammateSend(target: ReportNotifyTarget): boolean {
  return target.kind === "teammate" && target.outcome === "failed";
}

function checkIsDeliveredChannelPost(target: ReportNotifyTarget): boolean {
  return target.kind === "channel" && target.outcome === "delivered";
}

function checkIsFailedChannelPost(target: ReportNotifyTarget): boolean {
  return target.kind === "channel" && target.outcome === "failed";
}

/** A preview run's would-be copy: resolved, deliberately unsent. */
function checkIsPreviewTeammateSend(target: ReportNotifyTarget): boolean {
  return target.kind === "teammate" && target.reason === "preview";
}

function checkIsPreviewChannelPost(target: ReportNotifyTarget): boolean {
  return target.kind === "channel" && target.outcome === "skipped";
}

/**
 * How many distinct people a teammate predicate matched. The pass records one
 * row per (report, recipient), so a subscriber copied on every report has a row
 * per report — counting rows would report them as that many recipients.
 */
function countTeammates(
  targets: readonly ReportNotifyTarget[],
  checkIsMatch: (target: ReportNotifyTarget) => boolean,
): number {
  return new Set(
    targets.flatMap((target) =>
      target.kind === "teammate" && checkIsMatch(target)
        ? [target.teammate_id]
        : [],
    ),
  ).size;
}

/**
 * The notify row's one-line summary, in precedence order. Teammate sends and
 * channel posts fail independently, so each failure names its own side: a
 * broken channel post must not report the teammates it never touched as
 * unnotified. Every count here is of what actually happened, so a partial
 * failure reports the people (or posts) that broke rather than everyone
 * addressed.
 */
function describeNotifyRow({
  deliveredCount,
  failedSendCount,
  channelPostCount,
  failedChannelPostCount,
  previewSendCount,
  previewChannelPostCount,
  providerLabel,
}: {
  deliveredCount: number;
  failedSendCount: number;
  channelPostCount: number;
  failedChannelPostCount: number;
  previewSendCount: number;
  previewChannelPostCount: number;
  providerLabel: string;
}): string {
  if (failedSendCount > 0) {
    return `Failed to notify ${pluralize(failedSendCount, "recipient")}`;
  }
  if (failedChannelPostCount > 0) {
    return `Failed to post ${pluralize(failedChannelPostCount, "message")} to ${providerLabel}`;
  }
  if (channelPostCount > 0) {
    return `Posted ${pluralize(channelPostCount, "message")} to ${providerLabel}`;
  }
  if (previewChannelPostCount > 0) {
    return `Preview — would post ${pluralize(previewChannelPostCount, "message")} to ${providerLabel}`;
  }
  if (previewSendCount > 0) {
    return `Preview — would notify ${pluralize(previewSendCount, "recipient")}`;
  }
  if (deliveredCount === 0) {
    return "No notifications sent";
  }
  return `Notified ${pluralize(deliveredCount, "recipient")}`;
}

const NotifyTool: ToolComponent<unknown, ReportNotifyOutput> = ({
  status,
  output,
}) => {
  const { teammateNamesById } = useRunTranscriptContext();
  if (status.type === "complete" && output !== undefined) {
    // A failed call's error-shaped output falls through to the failed row.
    const parsed = reportNotifyOutputSchema.safeParse(output);
    if (parsed.success && !checkIsErrorToolOutput(parsed.data)) {
      const notifyOutput = parsed.data;
      const { targets } = notifyOutput;
      const providerLabel =
        DELIVERY_PROVIDER_LABELS[notifyOutput.delivery_provider];
      const label = describeNotifyRow({
        // Teammate counts are people (deduped across reports); channel counts
        // are posts, where one row per (report, channel) is one real post.
        deliveredCount: countTeammates(targets, checkIsDeliveredTeammate),
        failedSendCount: countTeammates(targets, checkIsFailedTeammateSend),
        channelPostCount: targets.filter(checkIsDeliveredChannelPost).length,
        failedChannelPostCount: targets.filter(checkIsFailedChannelPost).length,
        previewSendCount: countTeammates(targets, checkIsPreviewTeammateSend),
        previewChannelPostCount: targets.filter(checkIsPreviewChannelPost)
          .length,
        providerLabel,
      });
      const rowStatus: ChatToolStatus =
        notifyOutput.status === "failed" ||
        targets.some((target) => target.outcome === "failed")
          ? { type: "incomplete" }
          : status;

      // Nothing was addressed (a preview, or a run that notified no one) — no
      // breakdown to reveal, so keep the compact row.
      if (targets.length === 0) {
        return <ChatToolBase status={rowStatus}>{label}</ChatToolBase>;
      }
      return (
        <ChatToolDisclosure status={rowStatus} label={label}>
          <NotifyTargetsBreakdown
            targets={targets}
            providerLabel={providerLabel}
            teammateNamesById={teammateNamesById}
          />
        </ChatToolDisclosure>
      );
    }
  }
  if (status.type !== "running") {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        Failed to send notifications
      </ChatToolBase>
    );
  }
  return <ChatToolBase status={status}>Sending notifications</ChatToolBase>;
};

export function classifyNotifyOutput(
  output: unknown,
):
  | { kind: "current"; output: ReportNotifyOutput }
  | { kind: "legacy"; output: ManagerReportNotifyOutput }
  | null {
  const current = reportNotifyOutputSchema.safeParse(output);
  if (current.success && !checkIsErrorToolOutput(current.data)) {
    return { kind: "current", output: current.data };
  }
  const legacy = managerReportNotifyOutputSchema.safeParse(output);
  if (legacy.success && !checkIsErrorToolOutput(legacy.data)) {
    return { kind: "legacy", output: legacy.data };
  }
  return null;
}

const MANAGER_REPORT_PLAN_COPY: PlanCopy = {
  running: "Finding managers with call activity",
  failed: "Couldn't find managers with call activity",
  done: (reportNames) =>
    `Found ${pluralize(reportNames.length, "manager")} with call activity`,
};

/** Manager-report delegation copy, per the template's subject noun. */
export function managerDelegationCopy(subject: string): DelegationCopy {
  return {
    resolveName: (reportKey, teammateNamesById, reportNamesByKey) => {
      const decoded = decodeInstanceReportKey(reportKey);
      const managerName =
        decoded?.instance.type === "manager"
          ? teammateNamesById.get(decoded.instance.manager_id)
          : undefined;
      return reportNamesByKey.get(reportKey) ?? managerName ?? "a manager";
    },
    running: (name) =>
      name === null
        ? `Detecting ${subject}`
        : `Detecting ${subject} for ${name}`,
    generated: (name) => `Generated ${subject} alert for ${name}`,
    empty: (name) => `No ${subject} detected for ${name}`,
    failed: (name) => `Failed to generate ${subject} alert for ${name}`,
    released: (name) => `${name} is no longer part of this run`,
    notReady: (name) => `${name}'s report is not ready to generate yet`,
  };
}

/** Builds the plan/delegate/notify registry for a manager-report template. */
function buildManagerReportTools(subject: string): ChatToolComponents {
  return {
    byName: {
      [REPORT_PLAN_TOOL_ID]: guardToolRender(
        makePlanTool(MANAGER_REPORT_PLAN_COPY),
      ),
      [REPORT_DELEGATE_TOOL_ID]: guardToolRender(
        makeDelegateTool(managerDelegationCopy(subject)),
      ),
      [REPORT_NOTIFY_TOOL_ID]: guardToolRender(NotifyToolCompat),
    },
    Fallback: FallbackTool,
  };
}

const SCORE_MOVEMENT_TOOLS = buildManagerReportTools("score movement");
const LEAD_BURNING_TOOLS = buildManagerReportTools("lead burning");

/** Renderer for the notification output persisted before target-based results. */
const LegacyNotifyTool: ToolComponent<unknown, ManagerReportNotifyOutput> = ({
  status,
  output,
}) => {
  const { teammateNamesById } = useRunTranscriptContext();
  if (status.type === "complete") {
    if (output !== undefined && !checkIsErrorToolOutput(output)) {
      const recipientCount =
        output.emailed_recipient_teammate_ids.length +
        output.failed_recipient_teammate_ids.length;
      if (output.status === "failed") {
        return (
          <ChatToolBase status={{ type: "incomplete" }}>
            Failed to notify {pluralize(recipientCount, "recipient")}
          </ChatToolBase>
        );
      }
      const providerLabel = DELIVERY_PROVIDER_LABELS[output.delivery_provider];
      const skippedNote = describeRecipientsWithoutChatUser(
        output.recipients_without_chat_user,
        teammateNamesById,
        providerLabel,
      );
      if (output.channel_post_count > 0) {
        return (
          <ChatToolBase status={status}>
            Posted {pluralize(output.channel_post_count, "message")} to{" "}
            {providerLabel}
          </ChatToolBase>
        );
      }
      return (
        <ChatToolBase status={status}>
          {recipientCount === 0
            ? "No notifications sent"
            : `Notified ${pluralize(recipientCount, "recipient")}`}
          {skippedNote ? ` · ${skippedNote}` : null}
        </ChatToolBase>
      );
    }
  }
  if (status.type !== "running") {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        Failed to send notifications
      </ChatToolBase>
    );
  }
  return <ChatToolBase status={status}>Sending notifications</ChatToolBase>;
};

/**
 * Plan/delegation copy for a custom run. The plan row enumerates who the run
 * reports on; each delegation row names its subject from the plan ("Tom",
 * "Mary Example's team"). A shared-report run has no subject to name — its
 * `resolveName` returns "" and every label falls back to "the report".
 */
const PLAN_COPY: PlanCopy = {
  running: "Planning the run",
  failed: "Failed to plan the run",
  done: (reportNames) => {
    if (reportNames.length === 0) {
      return "Planned the run — no reports to generate for this window";
    }
    return `Planned ${pluralize(reportNames.length, "report")}: ${formatNameList(reportNames)}`;
  },
};

const DELEGATION_COPY: DelegationCopy = {
  resolveName: (reportKey, teammateNamesById, reportNamesByKey) => {
    if (reportKey === SHARED_REPORT_KEY) {
      return "";
    }
    const decoded = decodeInstanceReportKey(reportKey);
    const teammateName =
      decoded?.instance.type === "teammate"
        ? teammateNamesById.get(decoded.instance.teammate_id)
        : decoded?.instance.type === "manager"
          ? teammateNamesById.get(decoded.instance.manager_id)
          : undefined;
    return reportNamesByKey.get(reportKey) ?? teammateName ?? "";
  },
  running: (name) =>
    name ? `Generating the report for ${name}` : "Generating the report",
  generated: (name) =>
    name ? `Generated the report for ${name}` : "Generated the report",
  empty: (name) =>
    name
      ? `Report saved for ${name} — nothing qualified`
      : "Report saved — nothing qualified",
  failed: (name) =>
    name
      ? `Failed to generate the report for ${name}`
      : "Failed to generate the report",
  released: (name) =>
    name
      ? `${name} is no longer part of this run`
      : "The report is no longer part of this run",
  notReady: (name) =>
    name
      ? `The report for ${name} is waiting on its group's reports`
      : "The report's inputs are not generated yet",
};

/**
 * New runs emit the shared per-target notification output. Historical manager
 * reports and custom runs persisted the aggregate legacy shape, so dispatch to
 * the matching renderer without rewriting their transcripts.
 */
function NotifyToolCompat(props: ToolComponentProps<unknown, unknown>) {
  const classified = classifyNotifyOutput(props.output);
  if (classified?.kind === "current") {
    return <NotifyTool {...props} output={classified.output} />;
  }
  if (classified?.kind === "legacy") {
    return <LegacyNotifyTool {...props} output={classified.output} />;
  }
  return <NotifyTool {...props} output={undefined} />;
}

/**
 * A custom run's main thread is the shared report orchestrator (plan →
 * delegate → notify). The research tools stay registered so transcripts from
 * before the migration — where the custom automation researched and saved the
 * report itself — still render.
 */
const CUSTOM_RUN_TOOLS: ChatToolComponents = {
  byName: {
    ...RESEARCH_THREAD_TOOLS.byName,
    [REPORT_PLAN_TOOL_ID]: guardToolRender(makePlanTool(PLAN_COPY)),
    [REPORT_DELEGATE_TOOL_ID]: guardToolRender(
      makeDelegateTool(DELEGATION_COPY),
    ),
    [REPORT_NOTIFY_TOOL_ID]: guardToolRender(NotifyToolCompat),
  },
  Fallback: RESEARCH_THREAD_TOOLS.Fallback,
};

/** The tool registry for a run's main thread, by template. */
export function toolsForTemplate(
  templateId: AutomationTemplateId,
): ChatToolComponents {
  switch (templateId) {
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.STANDOUT_CALLS:
    case AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT:
    case AutomationTemplateIds.CUSTOM:
      return CUSTOM_RUN_TOOLS;
    case AutomationTemplateIds.LEAD_BURNING:
      return LEAD_BURNING_TOOLS;
    default:
      return SCORE_MOVEMENT_TOOLS;
  }
}

export interface OrchestratorTranscriptProps {
  playbookId: string | undefined;
  automationId: AutomationId;
  /** Selects the tool renderers for the run's main thread. */
  templateId: AutomationTemplateId;
  runId: AutomationRunId;
  isRunTerminal: boolean;
  messages: readonly UIMessage[];
  /** True while the orchestrator's thread is still streaming in. */
  isStreaming?: boolean;
  /** Display names keyed by teammate id, for manager labels. */
  teammateNamesById: ReadonlyMap<string, string>;
  /** Styleguide override supplying subagent threads statically (defaults to live/fetched). */
  resolveThreadMessages?: ResolveThreadMessages;
  /** Called when a report is saved live, so the app can refresh results. */
  onArtifactSaved?: (artifactId: AutomationArtifactId) => void;
}

/**
 * The orchestrator's conversation, rendered as a plain chat thread — the same
 * `RunTranscript` any agent thread uses, with a tool renderer per
 * orchestrator tool. The plan and notification calls are ordinary tool-status
 * lines; each delegation call is a tool row that happens to expand into that
 * manager's subagent thread (discovered from the call's input, so a running
 * subagent streams live before the delegation has output). Provides the
 * `RunTranscriptContext` the static tool components (and the nested subagent
 * transcripts) read from.
 */
export const OrchestratorTranscript: FC<OrchestratorTranscriptProps> = (
  props,
) => {
  const {
    playbookId,
    automationId,
    templateId,
    runId,
    isRunTerminal,
    messages,
    isStreaming,
    teammateNamesById,
    resolveThreadMessages,
    onArtifactSaved,
  } = props;

  const reportNamesByKey = useMemo(
    () => buildReportNamesByKey(messages),
    [messages],
  );

  return (
    <RunTranscriptProvider
      playbookId={playbookId}
      automationId={automationId}
      runId={runId}
      isRunTerminal={isRunTerminal}
      teammateNamesById={teammateNamesById}
      reportNamesByKey={reportNamesByKey}
      resolveThreadMessages={resolveThreadMessages}
      onArtifactSaved={onArtifactSaved}
    >
      <RunTranscript
        messages={messages}
        isStreaming={isStreaming}
        tools={toolsForTemplate(templateId)}
      />
    </RunTranscriptProvider>
  );
};
